import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";
import { ArrowRight } from "@phosphor-icons/react";

const cases = [
  {
    id: "freelance-designer",
    persona: "Freelance designer",
    title: "Sending 200 MB of Figma exports to a client",
    body: [
      "Your client doesn't want a Dropbox link cluttering their inbox forever. They want the file, today, on a phone, while they're between meetings. SendMe gives you a 6-digit code that you can read out on a call: 'four-eight-two-nine-three-one'. They type it, they download, they confirm. The link expires before they finish their lunch break.",
      "If the export contains unreleased product visuals, add a password. Set expiry to 4 hours — long enough that they can re-download if they accidentally close the tab, short enough that nothing leaks into the company chat archive."
    ]
  },
  {
    id: "lawyer-contracts",
    persona: "Independent lawyer",
    title: "Sharing a signed PDF with opposing counsel",
    body: [
      "Lawyers historically email contracts as attachments. The problem: that email lives forever, gets forwarded between paralegals, and the metadata (last modified, author) often leaks more than the content. With SendMe, you upload the signed PDF, set a 24-hour expiry, add a password, and send the code by SMS plus the password by phone call.",
      "When the deadline passes, the file disappears. The receiving counsel has it locally if they downloaded, and there's no copy floating in cloud space that an intern could stumble across."
    ]
  },
  {
    id: "photographer-gallery",
    persona: "Wedding photographer",
    title: "Delivering 4 GB of edited photos to a couple",
    body: [
      "Galleries from full-day shoots routinely hit 3-5 GB. WeTransfer charges you for that; Dropbox needs both parties to install something; emailing is impossible. With SendMe Pro, you upload the entire folder as a single multi-file transfer, set expiry to 7 days, and share the code on WhatsApp.",
      "The couple downloads when they're ready. Our backend streams the files in parallel, so a typical 4 GB gallery on a decent connection lands in under 8 minutes. After 7 days, the link is gone and your storage isn't paying rent on photos that have already been delivered."
    ]
  },
  {
    id: "hr-onboarding",
    persona: "HR manager",
    title: "Sending offer letters and IDs to new hires",
    body: [
      "Onboarding documents are sensitive — offer letters with salary, copies of ID, signed NDAs. Email is too persistent. A shared drive folder requires the new hire to have a company account before they've started.",
      "SendMe lets you upload the document set, password-protect it, and SMS the 6-digit code to the candidate's personal phone. They open it on their device, download once, and the link disappears in 24 hours. No company account required, no documents sitting in personal inboxes."
    ]
  },
  {
    id: "doctor-reports",
    persona: "Doctor",
    title: "Sharing a scan with a specialist colleague",
    body: [
      "Medical files are big, sensitive, and time-pressured. WhatsApp compresses scans into uselessness. Email attachments often exceed quotas. SendMe accepts the original DICOM zip, lets you set a 6-hour expiry, password-protects with the patient's date of birth (which only the patient and you should know), and gives you a code to read out on a phone consult.",
      "After the consult, the file disappears from our servers. The specialist either downloaded a local copy or didn't — either way, it isn't sitting on a third-party server indefinitely."
    ]
  },
  {
    id: "journalist-source",
    persona: "Journalist receiving from a source",
    title: "Receiving leaked documents securely",
    body: [
      "Sources frequently send documents through whatever's easiest, which is rarely the most secure. By telling your source to use SendMe with a short expiry, you reduce the time window during which the document exists on a third-party server. You receive the code on Signal, download immediately, store locally on an encrypted drive, and the SendMe-side copy disappears within hours.",
      "Important caveat: SendMe is not end-to-end encrypted by default. For truly high-stakes leaks (think state-actor adversaries), pair SendMe with client-side encryption — your source encrypts a ZIP with a strong password, sends the encrypted ZIP via SendMe, and shares the ZIP password through a third channel like a meeting."
    ]
  },
  {
    id: "video-editor",
    persona: "Video editor",
    title: "Sending preview cuts to a client across timezones",
    body: [
      "Your client is in Los Angeles, you're in Mumbai. They want to review the cut, give notes, and you want them to be able to re-download if they didn't fully save it the first time. SendMe Pro's 7-day expiry covers a full review-and-revise cycle without the file outliving its usefulness.",
      "Use multi-file mode to bundle the 1080p preview, the storyboard PDF, and a notes-template DOCX into one code. They get everything in one shot, even from a hotel Wi-Fi connection."
    ]
  },
  {
    id: "student-group",
    persona: "Student group project",
    title: "Coordinating files between 5 group members",
    body: [
      "Group projects on free Google accounts get complicated when someone runs out of storage. A team workspace on SendMe (₹699/month split five ways = ₹140 per person) gives you 5 GB of shared space and a Drive-style interface without anyone needing to share their personal Drive.",
      "When the semester ends, you delete the workspace and the files are gone. No lingering 'GroupProject_Final_v17_FINAL.docx' haunting your account three years later."
    ]
  },
  {
    id: "remote-team",
    persona: "Distributed startup (8 people)",
    title: "Sharing build artifacts and design exports across timezones",
    body: [
      "Eight engineers and designers, scattered across India, Europe, and Brazil. Slack file uploads expire after 90 days on the free plan. A team workspace becomes the durable layer: the engineering team drops build artifacts into /builds/, design drops exports into /design/, and everyone has a stable place to grab them.",
      "Mobile OTP gating on team join prevents accidental access from people outside the company. The 10-member cap matches the size of the team without enterprise pricing creeping in."
    ]
  },
  {
    id: "founder-investor",
    persona: "Startup founder",
    title: "Sending a pitch deck to an angel investor",
    body: [
      "Pitch decks contain financials you'd rather not see screenshotted into a WhatsApp group. SendMe lets you send a 24-hour expiring link with a password — the investor downloads when they're ready, and there's no permanent 'deck.pdf' link in their inbox that they could accidentally forward.",
      "After the meeting, you'll re-share an updated v2 anyway. Why leave v1 floating in three different inboxes?"
    ]
  }
];

export default function UseCases() {
  return (
    <PageShell
      eyebrow="Use cases"
      title="Real workflows, real users."
      subtitle="Ten ways people actually use SendMe — beyond the dropzone demo. If your work is anywhere on this list, you probably have a SendMe-shaped problem we can solve."
    >
      <PageMeta
        title="Use cases · Real workflows, real users"
        description="10 detailed SendMe workflows: freelance designers, wedding photographers, lawyers, HR onboarding, doctors, journalists, distributed startups, and more."
      />
      <div className="space-y-10" data-testid="use-cases-list">
        {cases.map((c, i) => (
          <article
            key={c.id}
            data-testid={`use-case-${c.id}`}
            className="brut-card p-6 md:p-8 grid md:grid-cols-[160px_1fr] gap-6"
          >
            <div>
              <div className="small-caps mb-1">#{String(i + 1).padStart(2, "0")}</div>
              <div className="font-bold tracking-tight text-[15px] mb-2">{c.persona}</div>
              <Link to="/" className="text-xs uppercase tracking-widest text-[#FF3366] inline-flex items-center gap-1">
                Try it <ArrowRight size={11} weight="bold" />
              </Link>
            </div>
            <div>
              <h2 className="text-2xl tracking-tight mb-4">{c.title}</h2>
              {c.body.map((p, j) => (
                <p key={j} className="text-[16px] leading-[1.75] text-black/80 mb-4 last:mb-0">{p}</p>
              ))}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-16 brut-card p-7 text-center max-w-2xl mx-auto">
        <div className="small-caps mb-2 text-[#FF3366]">Your use case isn't here?</div>
        <h3 className="text-2xl tracking-tight mb-3">Tell us about it.</h3>
        <p className="text-black/65 mb-5">
          We update this page based on what real users actually do with SendMe. Write to <a href="mailto:hello@sendme.co.in" className="underline">hello@sendme.co.in</a> and your workflow might be the next case study.
        </p>
        <Link to="/" className="btn-brut-primary inline-flex items-center gap-2">Start sending <ArrowRight size={16} weight="bold"/></Link>
      </div>
    </PageShell>
  );
}
